import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Models } from '@ibexfusion/models';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { PackageService } from '@services/packages/package.service';
import { AssetSelectorComponent } from '../../../components/asset-selector/asset-selector.component';
import { TranslateService } from '@services/translate/translate.service';
import { EnvironmentService } from '@services/environment/environment.service';
import { PageAction } from '../../../components/page-actions/page-actions.component';
import { ImageAssetsService } from '@services/assets/image-assets.service';
import { MediaAssetsService } from '@services/assets/media-assets.service';
import { PrivilegeGuard } from '../../../guards/privilege.guard';
import { QueService } from '@services/que/que.service';
import { SelectAssetsComponent } from '../../../components/select-assets/select-assets.component';

import JsonPointer from 'jsonpointer';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { VideoAssetsService } from '@services/assets/video-assets.service';
import { EditMediaComponent } from '../../dynamic-content/dynamic-content-edit/components/edit-media/edit-media.component';

@Component({
    selector: 'app-package-screen-images',
    templateUrl: './package-screen-images.component.html',
    styleUrls: ['./package-screen-images.component.scss'],
    providers: [PackageService, MediaAssetsService, ImageAssetsService, QueService, VideoAssetsService],
    standalone: false
})
export class PackageScreenImagesComponent implements OnInit, OnDestroy {

  public packageId;
  public myPackage: Models.Package;

  @Input() onSave: Observable<any>;
  @Input() noHeader = false;
  @HostBinding('class.no-header') get isNoHeader() {
    return this.noHeader;
  }
  public messages;
  public imageAssetPath: any;
  public videoAssetPath: any;
  public mediaAssetPath: any;
  public activeEmoImage = -1;
  public activeScreenImage = -1;
  private subscription: any;
  private now = { m: new Date().getMonth(), d: new Date().getDate() };
  public navigationPoints = [];
  public activeNavigation: string;
  public assetByPath = {};
  pageActions: any;

  constructor(
    private route: ActivatedRoute, private snackbar: SnackbarService, public packageService: PackageService, private dialog: MatDialog, private ts: TranslateService,
    private environmentService: EnvironmentService, private imageService: ImageAssetsService, private mediaService: MediaAssetsService, private priv: PrivilegeGuard,
    private que: QueService, private videoService: VideoAssetsService
  ) {
    this.imageAssetPath = this.environmentService.getImageAssetPath();
    this.mediaAssetPath = this.environmentService.getMediaAssetPath();
    this.videoAssetPath = this.environmentService.getVideoAssetPath();
    this.pageActions = [ new PageAction({ icon: 'save', title: '', translationString: 'general.save', callback: () => { this.save(); } }), new PageAction({
      icon: 'language', title: '', translationString: 'general.queuePriority', hidden: !this.priv.checkIfPrivilege('write', 'packages'),
      callback: () => { this.que.triggerPriority(this.packageId); }
    }) ];
  }

  async ngOnInit() {
    this.messages = this.ts.get('system');
    this.packageId = this.route.snapshot.params['id'];
    this.myPackage = await this.packageService.get(this.packageId);
    this.navigationPoints = [
      this.myPackage?.ibexTouch?.enabled ? 'ibextouch' : null, this.myPackage?.ibexApp?.enabled ? 'ibexsmart' : null, this.myPackage?.ibexTablet?.enabled ? 'ibextablet' : null
    ].filter(Boolean);
    const paths = this.getAssetPaths().filter(el => !!el);
    const media = await this.mediaService.find(0, 1000, { path: { $in: paths } });
    const image = await this.imageService.find(0, 1000, { path: { $in: paths } });
    const video = await this.videoService.find(0, 1000, { path: { $in: paths } });
    const assets = [...media.data, ...image.data, ...video.data];
    for (const asset of assets) {
      this.assetByPath[asset.path] = asset;
    }
    this.activeNavigation = this.navigationPoints[0];
    this.setActiveImage('emo');
    this.setActiveImage('screen');
  }

  getAssetPaths() {
    let paths = [this.myPackage.screen.favicon, this.myPackage.screen.mobileLogo, this.myPackage.screen.tabletLogo, this.myPackage.screen.screenLogo];
    paths = paths.concat(this.myPackage.screen.emoImages.map(el => el.url)).concat(this.myPackage.screen.screenImages.map(el => el.urlHome)).concat(this.myPackage.screen.tabletImages.map(el => el.url));
    return paths.map(el => !!el ? this.getPath(el) : '');
  }

  getPath(el) {
    if (!el) return;
    const parts = el.split('?')[0].split('/');
    return (el.includes('.jpg') || el.includes('.png') || el.includes('/videos/')) ? `${parts[parts.length - 2]}/${parts[parts.length - 1]}` : `${parts[parts.length - 3]}/${parts[parts.length - 2]}`;
  }

  getAsset(val) {
    return this.assetByPath[this.getPath(val)];
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe?.();
  }

  public async save(): Promise<any> {
    this.myPackage.screen.emoImages = this.myPackage.screen.emoImages.filter(img => img.assetId);
    for (let i = this.myPackage.screen.screenImages.length - 1; i >= 0; i--) {
      if (!this.myPackage.screen.screenImages[i].assetIdHome && !this.myPackage.screen.screenImages[i].assetIdApp) {
        this.myPackage.screen.screenImages.splice(i, 1);
      } else if (!this.myPackage.screen.screenImages[i].assetIdHome || !this.myPackage.screen.screenImages[i].assetIdApp) {
        if (!!this.myPackage.screen.screenImages[i].assetIdHome && !this.myPackage.screen.screenImages[i].assetIdApp) {
          this.myPackage.screen.screenImages[i].assetIdApp = this.myPackage.screen.screenImages[i].assetIdHome;
        } else {
          this.snackbar.open('could not save the screen images, because you need both home and app screen! You can use the same image if you like.');
          return;
        }
      }
    }
    await this.packageService.update(this.myPackage._id, this.myPackage);
    this.setActiveImage('emo');
    this.setActiveImage('screen');
    this.snackbar.success(this.messages.general.savedChanges, 2000);
  }

  async chooseFromImageAssets(targetKey, postfix = ''): Promise<any> {
    const dialogRef: MatDialogRef<AssetSelectorComponent> = this.dialog.open(AssetSelectorComponent, { width: '75%', height: '75%', data: { type: 'image' } });
    const asset: Models.ImageAsset = await dialogRef.afterClosed().toPromise();
    if (!asset) {
      return;
    }
    const format = JsonPointer.get(this.myPackage, targetKey + '/format') || 'raw';
    const url = this.imageAssetPath + asset.path + '/' + format + '?t=' + Date.now();
    JsonPointer.set(this.myPackage, targetKey + '/url' + postfix, url);
    JsonPointer.set(this.myPackage, targetKey + '/assetId' + postfix, asset._id);
  }

  async chooseFromMediaAssets(targetKey): Promise<any> {
    const assets: Models.ImageAsset[] = await this.getAssets('Anderes', false, 'media');
    if (!!assets?.[0]) {
      JsonPointer.set(this.myPackage, targetKey, this.mediaAssetPath + assets[0].path + '?t=' + Date.now());
    }
  }

  async getAssets(title, multiple, type): Promise<Models.ImageAsset[]> {
    return await this.dialog.open(SelectAssetsComponent, {
      width: '880px', height: '75%', panelClass: 'select-assets-modal', data: { type, multiple, title, page: 'package-image'}
    }).afterClosed().toPromise();
  }

  public async editMedia(asset) {
    await this.dialog.open(EditMediaComponent, { width: '875px', height: 'auto', panelClass: 'edit-media-modal', data: { asset: asset, isInContent: true, page: 'images' } })
      .afterClosed().toPromise();
  }

  async addImage(type, targetKey, postfix = '') {
    const assets: Models.ImageAsset[] = await this.getAssets('Bilder', true, 'image');
    for (let asset of (assets || [])) {
      let item: any = new Models.EmoImage();
      if (type === 'screen') {
        item = new Models.ScreenImage();
      }
      const year = new Date().getFullYear();
      item.from = new Date(year, 0, 1);
      item.to = new Date(year, 11, 31);
      const format = JsonPointer.get(this.myPackage, targetKey + '/format') || 'sixteenToNine';
      item['url' + postfix] = this.imageAssetPath + asset.path + '/' + format + '?t=' + Date.now();
      item['assetId' + postfix] = asset._id;
      this.assetByPath[asset.path] = asset;
      this.myPackage.screen[type + 'Images']?.push(item);
    }
  }

  async addVideo(type, postfix = '') {
    const assets: any[] = await this.getAssets('Videos', true, 'video');
    for (let asset of (assets || [])) {
      let item: any = new Models.EmoImage();
      if (type === 'screen') {
        item = new Models.ScreenImage();
      }
      const year = new Date().getFullYear();
      item.from = new Date(year, 0, 1);
      item.to = new Date(year, 11, 31);
      item['url' + postfix] = this.videoAssetPath + asset.path;
      item['assetId' + postfix] = asset._id;
      item.type = 'video';
      item.duration = asset.duration;
      this.assetByPath[asset.path] = asset;
      this.myPackage.screen[type + 'Images']?.push(item);
    }
  }

  removeImage(type, index) {
    this.myPackage.screen[type + 'Images']?.splice(index, 1);
  }

  setDisabled(field, index) {
    this.myPackage.screen[field][index].disabled = !this.myPackage.screen[field][index].disabled
  }

  setActiveImage(type) {
    let imageArray = type === 'emo' ? this.myPackage.screen.emoImages : (type === 'screen' ? this.myPackage.screen.screenImages : null);
    type === 'emo' ? this.activeEmoImage = -1 : (type === 'screen' ? this.activeScreenImage = -1 : null);

    const active = [];
    (imageArray || []).forEach((item, index) => {
      const from = item.from ? { m: item.from.getMonth(), d: item.from.getDate() } : null;
      const to = item.to ? { m: item.to.getMonth(), d: item.to.getDate() } : null;
      if (item.disabled || !from || !to) {
        return;
      }
      const info = {
        fromBeforeTo: from.m < to.m || (from.m === to.m && from.d <= to.d),
        fromBeforeNow: from.m < this.now.m || (from.m === this.now.m && from.d <= this.now.d),
        toAfterNow: to.m > this.now.m || (to.m === this.now.m && to.d >= this.now.d)
      };

      if (info.fromBeforeTo) {  // to is in same year
        if (info.fromBeforeNow && info.toAfterNow) {
          active.push({ index: index, from: from, to: to, info: info });
        }
      } else if (info.fromBeforeNow && (to.m < this.now.m || (to.m === this.now.m && to.d >= this.now.d))) { // to is next year and from is this year
        active.push({ index: index, from: from, to: to, info: info });
      } else if (!info.fromBeforeNow && info.toAfterNow) { // to is last year, from is this year
        active.push({ index: index, from: from, to: to, info: info });
      }
    });
    let min = -1;
    active.map(item => {
      let span = -2;
      const days = item.to.d >= item.from.d ? item.to.d - item.from.d : 31 - item.from.d + item.to.d;
      if (item.info.fromBeforeTo) {
        span = (item.to.m - item.from.m) * 100 + days;
      } else { // to is next year
        span = (item.from.m === item.to.m) ? 11 * 100 : (12 - item.from.m + item.to.m) * 100;
        span += days;
      }
      if (min === -1 || span <= min) {
        min = span;
        if (type === 'emo') {
          this.activeEmoImage = item.index;
          imageArray = this.myPackage.screen.emoImages;
        } else if (type === 'screen') {
          this.activeScreenImage = item.index;
        }
      }
    });
  }

  hover(item, event: any) {
    const imageContainer = document.createElement('div');
    const containerWidth = 500;
    const containerHeight = 500;
    imageContainer.id = 'media-img-preview-overlay'
    Object.assign(imageContainer.style, { position: 'absolute', width: `${containerWidth}px`, height: `${containerHeight}px`, zIndex: '1000', pointerEvents: 'none',
      display: 'flex', justifyContent: 'center', alignItems: 'center', left: `${event.clientX - containerWidth / 2}px`, top: `${Math.max(10, event.clientY - containerHeight)}px`
    });

    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' });

    imageContainer.appendChild(wrapper);

    const img = document.createElement('img');
    img.src = item;
    img.alt = 'Dynamic Image';
    Object.assign(img.style, {
      maxWidth: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'row', justifySelf: 'center', border: '5px solid white', borderRadius: '5px',
      boxShadow: 'rgba(0, 0, 0, 0.75) 0px 0px 10px', background: 'repeating-conic-gradient(#CCC 0 25%, #FFF 0 50%) 0 / 10px 10px'
    });
    wrapper.appendChild(img);
    document.body.appendChild(imageContainer);
  }

  leave() {
    document.getElementById('media-img-preview-overlay')?.remove();
  }

  public dropImage(event: any, field) {
    moveItemInArray(this.myPackage.screen[field], event.previousIndex, event.currentIndex);
  }
}
